import { useMemo } from 'react';
import { type Edge, type Node } from 'reactflow';
import dagre from 'dagre';

import { ErdModel, ErdNodeData, ErdRelationship } from '../types';

const NODE_WIDTH = 260;
const HEADER_HEIGHT = 48;
const COLUMN_ROW_HEIGHT = 22;
const PADDING_BOTTOM = 8;

function getConnectedColumnsForModel(
  modelId: string,
  relationships: ErdRelationship[],
): Set<string> {
  const cols = new Set<string>();
  for (const rel of relationships) {
    if (rel.sourceModelId === modelId && rel.sourceColumn) {
      cols.add(rel.sourceColumn.toLowerCase());
    }
    if (rel.targetModelId === modelId && rel.targetColumn) {
      cols.add(rel.targetColumn.toLowerCase());
    }
  }
  return cols;
}

function estimateNodeHeight(connectedColumnCount: number): number {
  const rows = Math.max(connectedColumnCount, 0);
  return HEADER_HEIGHT + rows * COLUMN_ROW_HEIGHT + PADDING_BOTTOM;
}

interface LayoutInput {
  models: ErdModel[];
  relationships: ErdRelationship[];
  selectedModelId: string | null;
  focusedModelId: string | null;
  highlightedModelId: string | null;
  onSelectModel: (modelId: string) => void;
  onEdgeClick: (relationship: ErdRelationship) => void;
}

interface LayoutOutput {
  nodes: Node<ErdNodeData>[];
  edges: Edge[];
}

const cssVar = (token: string) => `var(--color-${token})`;

const EDGE_COLORS = {
  relationshipTest: cssVar('fgBrand'),
  semanticLayer: cssVar('fgSuccess'),
  heuristicInferred: cssVar('fgWarning'),
  default: cssVar('fgDecorative'),
} as const;

function getEdgeStyle(
  edgeSource: ErdRelationship['edgeSource'],
  approvalStatus?: string,
) {
  switch (edgeSource) {
    case 'relationshipTest':
      return {
        stroke: EDGE_COLORS.relationshipTest,
        strokeWidth: 2,
        strokeDasharray: undefined,
      };
    case 'semanticLayer':
      return {
        stroke: EDGE_COLORS.semanticLayer,
        strokeWidth: 2,
        strokeDasharray: '8 4',
      };
    case 'heuristicInferred':
      return {
        stroke: EDGE_COLORS.heuristicInferred,
        strokeWidth: 2,
        strokeDasharray: approvalStatus === 'approved' ? undefined : '4 4',
      };
    default:
      return {
        stroke: EDGE_COLORS.default,
        strokeWidth: 1,
        strokeDasharray: undefined,
      };
  }
}

export function useErdLayout({
  models,
  relationships,
  selectedModelId,
  focusedModelId,
  highlightedModelId,
  onSelectModel,
  onEdgeClick,
}: LayoutInput): LayoutOutput {
  return useMemo(() => {
    if (models.length === 0) {
      return { nodes: [], edges: [] };
    }

    const relevantModels = focusedModelId
      ? getConnectedModels(focusedModelId, models, relationships)
      : models;

    const modelSet = new Set(relevantModels.map((m) => m.uniqueId));

    // Compute connected columns per model
    const connectedColsByModel = new Map<string, Set<string>>();
    for (const model of relevantModels) {
      connectedColsByModel.set(
        model.uniqueId,
        getConnectedColumnsForModel(model.uniqueId, relationships),
      );
    }

    // Layout with dagre (undirected feel: no rank direction bias)
    const g = new dagre.graphlib.Graph({ directed: false });
    g.setGraph({
      ranksep: 200,
      nodesep: 60,
      edgesep: 40,
    });
    g.setDefaultEdgeLabel(() => ({}));

    for (const model of relevantModels) {
      const connectedCols = connectedColsByModel.get(model.uniqueId) ?? new Set();
      const height = estimateNodeHeight(connectedCols.size);
      g.setNode(model.uniqueId, { width: NODE_WIDTH, height });
    }

    for (const rel of relationships) {
      if (modelSet.has(rel.sourceModelId) && modelSet.has(rel.targetModelId)) {
        g.setEdge(rel.sourceModelId, rel.targetModelId);
      }
    }

    dagre.layout(g);

    const nodes: Node<ErdNodeData>[] = relevantModels.map((model) => {
      const nodeData = g.node(model.uniqueId);
      const connectedCols = connectedColsByModel.get(model.uniqueId) ?? new Set();
      const height = estimateNodeHeight(connectedCols.size);
      const isDimmed =
        (selectedModelId !== null && selectedModelId !== model.uniqueId) ||
        (highlightedModelId !== null && highlightedModelId !== model.uniqueId);

      return {
        id: model.uniqueId,
        type: 'erdEntity',
        position: {
          x: (nodeData?.x ?? 0) - NODE_WIDTH / 2,
          y: (nodeData?.y ?? 0) - height / 2,
        },
        zIndex: 10,
        data: {
          model,
          connectedColumns: connectedCols,
          isSelected: selectedModelId === model.uniqueId,
          isFocused: focusedModelId === model.uniqueId,
          isHighlighted: highlightedModelId === model.uniqueId,
          isDimmed:
            selectedModelId !== null || highlightedModelId !== null ? isDimmed : false,
          onSelect: onSelectModel,
        },
      };
    });

    const edges: Edge[] = relationships
      .filter(
        (rel) => modelSet.has(rel.sourceModelId) && modelSet.has(rel.targetModelId),
      )
      .map((rel) => {
        const style = getEdgeStyle(rel.edgeSource, rel.approvalStatus);

        // Column-level handle IDs
        const sourceHandle = rel.sourceColumn
          ? `col-${rel.sourceColumn.toLowerCase()}`
          : undefined;
        const targetHandle = rel.targetColumn
          ? `col-${rel.targetColumn.toLowerCase()}`
          : undefined;

        return {
          id: rel.id,
          source: rel.sourceModelId,
          target: rel.targetModelId,
          sourceHandle,
          targetHandle,
          type: 'erdRelationship',
          label:
            rel.sourceColumn && rel.targetColumn
              ? `${rel.sourceColumn} \u2014 ${rel.targetColumn}`
              : rel.label,
          zIndex: 0,
          style: {
            stroke: style.stroke,
            strokeWidth: style.strokeWidth,
            strokeDasharray: style.strokeDasharray,
          },
          markerEnd: undefined,
          markerStart: undefined,
          data: {
            relationship: rel,
            overlayType:
              rel.edgeSource === 'relationshipTest'
                ? 'relationshipTests'
                : rel.edgeSource === 'semanticLayer'
                  ? 'semanticLayer'
                  : 'heuristicInferred',
            onEdgeClick,
          },
          animated:
            rel.edgeSource === 'heuristicInferred' && rel.approvalStatus === 'pending',
        };
      });

    return { nodes, edges };
  }, [
    models,
    relationships,
    selectedModelId,
    focusedModelId,
    highlightedModelId,
    onSelectModel,
    onEdgeClick,
  ]);
}

function getConnectedModels(
  focusId: string,
  allModels: ErdModel[],
  relationships: ErdRelationship[],
): ErdModel[] {
  const connectedIds = new Set<string>([focusId]);
  for (const rel of relationships) {
    if (rel.sourceModelId === focusId) connectedIds.add(rel.targetModelId);
    if (rel.targetModelId === focusId) connectedIds.add(rel.sourceModelId);
  }
  return allModels.filter((m) => connectedIds.has(m.uniqueId));
}
